// ADDED: the user configuration page, reached by clicking "Hi, {name}" in the nav bar.
// It shows the account details and lets the user change their password through the
// /changePassword endpoint, which already existed on the backend but had no screen.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axios, { getErrorMessage } from '../../api/axios';
import { useAuth } from '../Components/AuthProvider';
import { useTheme } from '../Components/ThemeProvider';

// Same rule the Login and Reset screens enforce, so the three stay consistent
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

export default function Account() {
    const navigate = useNavigate();
    const { user, logout, updateUser } = useAuth(); // MODIFIED: updateUser for the username card
    const { isDark, toggleTheme } = useTheme();

    // ADDED: the username other collectors search for. It starts as whatever the account
    // already has, so the field shows the current handle rather than an empty box.
    const [username, setUsername] = useState(user?.username || '');
    const [usernameMessage, setUsernameMessage] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [isSavingUsername, setIsSavingUsername] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        // Validated here as well as on the server, so the user gets the message instantly
        if (!currentPassword || !newPassword) {
            setErrorMessage('Please fill in your current and new password.');
            return;
        }
        if (!PASSWORD_REGEX.test(newPassword)) {
            setErrorMessage('New password must have 8+ characters, an uppercase letter, a number and a special character.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrorMessage('The new passwords do not match.');
            return;
        }
        if (newPassword === currentPassword) {
            setErrorMessage('Your new password has to be different from the current one.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post('/changePassword', { currentPassword, newPassword });
            setSuccessMessage(response.data.message || 'Password changed successfully.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            // 401 here means the *current* password was wrong, not that the session expired
            if (error.response?.status === 401) {
                setErrorMessage('Your current password is incorrect.');
            } else {
                setErrorMessage(getErrorMessage(error, 'Could not change your password.'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // The three password boxes, described once and rendered in a loop
    const passwordFields = [
        {
            id: 'currentPassword', label: 'Current password', value: currentPassword,
            onChange: setCurrentPassword, autoComplete: 'current-password',
            placeholder: 'Enter your current password',
        },
        {
            id: 'newPassword', label: 'New password', value: newPassword,
            onChange: setNewPassword, autoComplete: 'new-password',
            placeholder: 'Enter a new password',
            hint: 'At least 8 characters, with an uppercase letter, a number and a special character.',
        },
        {
            id: 'confirmPassword', label: 'Confirm new password', value: confirmPassword,
            onChange: setConfirmPassword, autoComplete: 'new-password',
            placeholder: 'Repeat the new password',
        },
    ];

    // Joined date, shown as a small detail so the page is not just a form
    const memberSince = user?.createdAt
        ? new Date(user.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
        : '—';

    // ADDED: saves a new handle through the same endpoint the sign-in gate uses, so there
    // is one set of rules for usernames rather than two.
    const saveUsername = async (event) => {
        event.preventDefault();
        setUsernameError('');
        setUsernameMessage('');
        setIsSavingUsername(true);

        try {
            const response = await axios.put('/me/username', { username: username.trim() });
            updateUser(response.data.user);
            setUsernameMessage('Username saved.');
        } catch (error) {
            setUsernameError(getErrorMessage(error, 'Could not save that username.'));
        } finally {
            setIsSavingUsername(false);
        }
    };

    return (
        <section className="cv-page min-h-screen py-8 px-4 sm:px-8">
            <div className="max-w-3xl mx-auto space-y-6 animate-fade-up">

                {/* Page header, matching the Models and Collections pages */}
                <div className="text-center space-y-2">
                    <p className="cv-eyebrow">Your account</p>
                    <h1 className="cv-display text-4xl sm:text-5xl">Garage settings</h1>
                    <p className="text-sm cv-muted">Manage how you sign in to SpeedVault.</p>
                </div>

                {/* Identity card: avatar initial, name, email, join date */}
                <div className="cv-card cv-speedline p-5 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="h-16 w-16 rounded-2xl bg-brand-flame text-white flex items-center justify-center shrink-0 mx-auto sm:mx-0">
                            <span className="font-display text-2xl font-bold">
                                {user?.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                        </div>
                        <div className="min-w-0 text-center sm:text-left">
                            <h2 className="cv-title text-2xl break-words">Hi, {user?.name || 'there'}</h2>
                            {/* MODIFIED: the handle sits above the email, since it is the one
                                other collectors actually see */}
                            {user?.username && <p className="text-sm cv-accent break-all">@{user.username}</p>}
                            <p className="text-sm cv-muted break-all">{user?.email}</p>
                            <p className="text-xs cv-muted mt-1">Collecting since {memberSince}</p>
                        </div>
                    </div>
                </div>

                {/* ADDED: the handle other collectors find you by. Changing it takes effect
                    immediately - anyone searching the old one stops finding you. */}
                <div className="cv-card p-5 sm:p-6">
                    <h2 className="cv-title text-lg mb-1">Username</h2>
                    <p className="text-sm cv-muted mb-4">
                        This is how other collectors find you on the Friends page. Your email address
                        is never shown to them.
                    </p>
                    <form onSubmit={saveUsername} className="space-y-3">
                        <div>
                            <label className="cv-label" htmlFor="account-username">Your username</label>
                            <input
                                id="account-username"
                                value={username}
                                onChange={(e) => { setUsername(e.target.value); setUsernameMessage(''); setUsernameError(''); }}
                                type="text"
                                autoComplete="off"
                                maxLength={20}
                                placeholder="e.g. redline_hunter"
                                className="cv-input"
                            />
                            <p className="text-xs cv-muted mt-1.5">
                                3 to 20 characters. Letters, numbers and underscores.
                            </p>
                        </div>

                        {usernameError && <p role="alert" className="cv-alert-error">{usernameError}</p>}
                        {usernameMessage && <p className="cv-alert-success">{usernameMessage}</p>}

                        <button
                            type="submit"
                            disabled={isSavingUsername || username.trim() === (user?.username || '') || username.trim().length < 3}
                            className="cv-btn cv-btn-outline disabled:opacity-50 disabled:cursor-not-allowed">
                            {isSavingUsername ? 'Saving...' : 'Save username'}
                        </button>
                    </form>
                </div>

                {/* Appearance: the same toggle as the nav bar, spelled out for phones
                    where the nav bar is tight on space */}
                <div className="cv-card p-5 sm:p-6">
                    <h2 className="cv-title text-lg mb-1">Appearance</h2>
                    <p className="text-sm cv-muted mb-4">Your choice is remembered on this device.</p>
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm cv-body">{isDark ? 'Dark mode' : 'Light mode'}</span>
                        <button
                            type="button"
                            onClick={toggleTheme}
                            role="switch"
                            aria-checked={isDark}
                            aria-label="Toggle dark mode"
                            className={`relative h-7 w-14 rounded-full transition-colors duration-300 shrink-0 ${isDark ? 'bg-brand-flame' : 'bg-gray-300'}`}
                        >
                            {/* MODIFIED: "left-0" added. Without it the knob started from its natural
                                spot, which inside a <button> is the centre, so translate-x-8 pushed it
                                past the right edge on desktop browsers. */}
                            <span className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 ${isDark ? 'translate-x-8' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>

                {/* Change password */}
                <div className="cv-card p-5 sm:p-6">
                    <h2 className="cv-title text-lg mb-1">Change password</h2>
                    <p className="text-sm cv-muted mb-4">You will stay signed in on this device after changing it.</p>

                    {errorMessage && <p role="alert" className="cv-alert-error mb-4">{errorMessage}</p>}
                    {successMessage && <p className="cv-alert-success mb-4">{successMessage}</p>}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {passwordFields.map((field) => (
                            <div key={field.id}>
                                <label className="cv-label" htmlFor={field.id}>{field.label}</label>
                                <input
                                    id={field.id}
                                    name={field.id}
                                    type="password"
                                    required
                                    autoComplete={field.autoComplete}
                                    placeholder={field.placeholder}
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    className="cv-input"
                                />
                                {field.hint && <p className="text-xs cv-muted mt-1">{field.hint}</p>}
                            </div>
                        ))}

                        {/* Stacks on phones, sits side by side from sm up */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-1">
                            <button type="submit" disabled={isSubmitting} className="cv-btn cv-btn-primary flex-1">
                                {isSubmitting ? 'Saving...' : 'Update password'}
                            </button>
                            <button type="button" onClick={() => navigate(-1)} className="cv-btn cv-btn-quiet flex-1">
                                Back
                            </button>
                        </div>
                    </form>
                </div>

                {/* Sign out, kept apart from the form so it cannot be hit by accident */}
                <div className="cv-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h2 className="cv-title text-lg">Sign out</h2>
                        <p className="text-sm cv-muted">Ends this session on this device.</p>
                    </div>
                    <button onClick={handleLogout} className="cv-btn cv-btn-outline w-full sm:w-auto">
                        Sign out
                    </button>
                </div>
            </div>
        </section>
    );
}
